/* Feb-2026 · Zernio Smart Composer.
 *
 * Dummy-proof one-screen cross-post wizard:
 *   1. Pick a preset (cover reveal / launch / sample / milestone / custom)
 *   2. Backend LLM writes platform-native captions (Claude Sonnet)
 *   3. Author picks platforms + edits any caption inline
 *   4. Optional paid-ad toggle per platform (Author Marketing Pro)
 *   5. Auto-mint short link on submit → clicks tracked to /r/{code}
 *   6. Publish everywhere in one click
 *
 * Pre-fills from an optional `initialMedia` (cover) and `synopsis`
 * (from the current project) so the author usually only picks a
 * preset before publishing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, Upload, Send, Wand2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '@/lib/api';

function usePresets(t) {
  return [
    { key: 'cover_reveal',    label: t('zernio.presetCoverReveal') },
    { key: 'launch_day',      label: t('zernio.presetLaunchDay') },
    { key: 'sample_chapter',  label: t('zernio.presetSampleChapter') },
    { key: 'milestone',       label: t('zernio.presetMilestone') },
    { key: 'custom',          label: t('zernio.presetCustom') },
  ];
}

const PLATFORMS_ORDER = ['instagram', 'tiktok', 'twitter', 'facebook', 'linkedin', 'threads', 'pinterest', 'youtube', 'bluesky'];

async function j(path, opts = {}) {
  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d?.detail?.message || d?.detail || `Request failed (${r.status})`);
  }
  return r.json();
}

async function upload(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API_BASE}/zernio/media/presign`, {
    method: 'POST', body: fd, credentials: 'include',
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || 'Upload failed');
  return r.json();
}

export default function ZernioSmartComposer({
  open, onOpenChange,
  bookId       = null,
  title        = '',
  synopsis     = '',
  initialMedia = null,
  destUrl      = null,
}) {
  const { t } = useTranslation();
  const PRESETS = usePresets(t);
  const [cfg, setCfg]           = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [preset, setPreset]     = useState('cover_reveal');
  const [customPrompt, setCustom] = useState('');
  const [captions, setCaptions] = useState({});
  const [platforms, setPlatforms] = useState({});   // platform -> accountId
  const [paidAd, setPaidAd]     = useState({});     // platform -> bool
  const [media, setMedia]       = useState(initialMedia);
  const [shortCode, setShort]   = useState(null);
  const [generating, setGen]    = useState(false);
  const [publishing, setPub]    = useState(false);
  const [uploading, setUp]      = useState(false);

  useEffect(() => {
    if (!open) return;
    setMedia(initialMedia);
    setCaptions({});
    setPlatforms({});
    setPaidAd({});
    setShort(null);
    j('/zernio/config').then(setCfg).catch(() => setCfg({ configured: false }));
    j('/zernio/accounts').then((r) => {
      const arr = r?.accounts || r?.data?.accounts || r || [];
      setAccounts(Array.isArray(arr) ? arr.filter((a) => a?.isActive !== false) : []);
    }).catch(() => setAccounts([]));
  }, [open, initialMedia]);

  const availablePlatforms = useMemo(() => {
    const set = new Set(accounts.map((a) => a.platform));
    return PLATFORMS_ORDER.filter((p) => set.has(p));
  }, [accounts]);

  const generate = async () => {
    if (!title) { toast.error(t('zernio.titleRequired')); return; }
    setGen(true);
    try {
      // Auto-mint short link on the very first generate so captions can embed it.
      let shortUrl = null;
      if (destUrl && !shortCode) {
        const link = await j('/clicks/short-links', {
          method: 'POST',
          body: JSON.stringify({
            dest_url: destUrl,
            label:    `${title} · ${preset}`,
            book_id:  bookId,
          }),
        });
        setShort(link.code);
        shortUrl = `${window.location.origin}/api/r/${link.code}`;
      } else if (shortCode) {
        shortUrl = `${window.location.origin}/api/r/${shortCode}`;
      }

      const r = await j('/zernio/captions/suggest', {
        method: 'POST',
        body:   JSON.stringify({
          preset,
          title,
          synopsis,
          short_url:     shortUrl,
          platforms:     availablePlatforms.length > 0 ? availablePlatforms : null,
          custom_prompt: preset === 'custom' ? customPrompt : null,
        }),
      });
      setCaptions(r.captions || {});
      // Auto-select every returned platform.
      const nextPicked = {};
      for (const p of Object.keys(r.captions || {})) {
        const acc = accounts.find((a) => a.platform === p);
        if (acc) nextPicked[p] = acc._id || acc.id || acc.accountId;
      }
      setPlatforms(nextPicked);
      toast.success(t('zernio.generatedCount', { count: Object.keys(r.captions || {}).length }));
    } catch (e) {
      toast.error(e.message || t('zernio.captionGenFailed'));
    } finally {
      setGen(false);
    }
  };

  const togglePlatform = (platform) => {
    const acc = accounts.find((a) => a.platform === platform);
    if (!acc) return;
    setPlatforms((cur) => {
      const next = { ...cur };
      if (next[platform]) delete next[platform];
      else next[platform] = acc._id || acc.id || acc.accountId;
      return next;
    });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    try {
      const r = await upload(f);
      setMedia({ url: r.public_url, type: r.type });
      toast.success(t('zernio.coverUploaded'));
    } catch (err) {
      toast.error(err.message || t('zernio.uploadFailed'));
    } finally {
      setUp(false);
    }
  };

  const publish = async () => {
    if (Object.keys(platforms).length === 0) {
      toast.error(t('zernio.pickPlatform'));
      return;
    }
    setPub(true);
    try {
      const selections = {};
      for (const [p, accId] of Object.entries(platforms)) {
        selections[p] = {
          account_id:  accId,
          paid_ad:     !!paidAd[p],
          custom_copy: captions[p] || null,
        };
      }
      await j('/zernio/posts', {
        method: 'POST',
        body:   JSON.stringify({
          content:     captions[Object.keys(platforms)[0]] || title,
          media:       media?.url ? [{ url: media.url, type: media.type || 'image' }] : [],
          selections,
          book_id:     bookId,
          utm_source:  'zernio',
          publish_now: true,
        }),
      });
      toast.success(t('zernio.publishedCount', { count: Object.keys(platforms).length }));
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.message || t('zernio.publishFailed'));
    } finally {
      setPub(false);
    }
  };

  const notConfigured = cfg && (!cfg.configured || !cfg.flag_on);
  const generated     = Object.keys(captions).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="zernio-smart-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Wand2 className="h-4 w-4 text-primary" /> {t('zernio.dialogTitle')}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            {t('zernio.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        {notConfigured ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-[11.5px] text-amber-900" data-testid="zsc-not-configured">
            {t('zernio.notConfigured')}
            <b> zernio_publishing </b> {t('zernio.notConfiguredSuffix')}
          </div>
        ) : (
          <div className="space-y-4">
            {/* 1 · Cover */}
            <section>
              <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">{t('zernio.step1Cover')}</div>
              {media?.url ? (
                <div className="flex items-center gap-3 rounded-md border border-border p-2">
                  <img src={media.url} alt="Cover"
                        className="h-20 w-auto rounded-sm border border-border" data-testid="zsc-cover-preview" />
                  <div className="text-[11.5px] text-muted-foreground flex-1">{t('zernio.coverPrefilled')}</div>
                  <Button size="sm" variant="outline" onClick={() => setMedia(null)}
                          data-testid="zsc-cover-clear">
                    <X className="h-3.5 w-3.5" /> {t('zernio.replace')}
                  </Button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-secondary/50 px-3 py-2 text-[12px] hover:bg-secondary">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span>{uploading ? t('zernio.uploading') : t('zernio.uploadCoverLabel')}</span>
                  <input type="file" className="hidden"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={onFile}
                          data-testid="zsc-cover-input" />
                </label>
              )}
            </section>

            {/* 2 · Preset */}
            <section>
              <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">{t('zernio.step2')}</div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPreset(p.key)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${preset === p.key ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-secondary'}`}
                    data-testid={`zsc-preset-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {preset === 'custom' && (
                <Textarea
                  rows={2}
                  value={customPrompt}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder={t('zernio.customPromptPlaceholder')}
                  className="mt-2 text-[12px]"
                  data-testid="zsc-custom-prompt"
                />
              )}
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[10.5px] text-muted-foreground">
                  {t('zernio.usesClaudeSonnet')}
                </p>
                <Button size="sm" onClick={generate} disabled={generating}
                        data-testid="zsc-generate-btn">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {generated ? t('zernio.regenerate') : t('zernio.writeMyCaptions')}
                </Button>
              </div>
            </section>

            {/* 3 · Captions */}
            {generated && (
              <section>
                <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">{t('zernio.step3')}</div>
                {availablePlatforms.length === 0 ? (
                  <div className="rounded-sm border border-dashed border-border p-3 text-center text-[11.5px] text-muted-foreground" data-testid="zsc-no-accounts">
                    {t('zernio.connectAccount')} <a href="https://zernio.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline">{t('zernio.zernioDashboard')}</a> {t('zernio.toPublish')}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availablePlatforms.map((p) => {
                      const selected = !!platforms[p];
                      return (
                        <div key={p} className={`rounded-md border p-2 text-[11.5px] ${selected ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => togglePlatform(p)}
                              className="flex items-center gap-2 font-semibold capitalize"
                              data-testid={`zsc-platform-toggle-${p}`}
                            >
                              <input type="checkbox" readOnly checked={selected}
                                      className="h-3.5 w-3.5" />
                              {p}
                            </button>
                            <label className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
                              <input
                                type="checkbox"
                                checked={!!paidAd[p]}
                                disabled={!selected}
                                onChange={(e) => setPaidAd({ ...paidAd, [p]: e.target.checked })}
                                data-testid={`zsc-paid-toggle-${p}`}
                              />
                              {t('zernio.boostWithPaidAd')}
                            </label>
                          </div>
                          <Textarea
                            rows={2}
                            value={captions[p] || ''}
                            onChange={(e) => setCaptions({ ...captions, [p]: e.target.value })}
                            disabled={!selected}
                            className="mt-1 text-[12px]"
                            data-testid={`zsc-caption-${p}`}
                          />
                          <div className="mt-0.5 flex items-center justify-between text-[9.5px] text-muted-foreground">
                            <span>{t('zernio.chars', { count: (captions[p] || '').length })}</span>
                            {shortCode && <span className="font-mono-stat">{window.location.origin}/api/r/{shortCode}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)} data-testid="zsc-cancel">
            {t('zernio.cancel')}
          </Button>
          <Button size="sm" onClick={publish} disabled={!generated || publishing || Object.keys(platforms).length === 0}
                  data-testid="zsc-publish-btn">
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {' '}{Object.keys(platforms).length > 0 ? t('zernio.publishToCount', { count: Object.keys(platforms).length }) : t('zernio.publishToSelected')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
